import type {
  AnketaFormRequest,
  AnketaSubmitResult,
  QuestionnaireDefinition,
  RecognizedCustomerView,
} from '@360face/domain';

/** Error with the API code (NETWORK when the request did not reach the server). */
export class AnketaApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

export interface AnketaApi {
  definition(locationId: string): Promise<QuestionnaireDefinition>;
  submit(locationId: string, form: AnketaFormRequest): Promise<AnketaSubmitResult>;
  /** «Is this you?» by phone (owner 28.10.2026): first name + initial only. */
  recognize(locationId: string, phone: string): Promise<RecognizedCustomerView>;
}

export function createAnketaApi(baseUrl = '', fetchImpl: typeof fetch = (...a) => fetch(...a)): AnketaApi {
  async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetchImpl(`${baseUrl}${path}`, {
        method,
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body: body ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(20_000),
      });
    } catch {
      throw new AnketaApiError(0, 'NETWORK');
    }
    const json = (await res.json().catch(() => ({}))) as { data?: T; error?: { code?: string } };
    if (!res.ok) throw new AnketaApiError(res.status, json.error?.code ?? 'UNKNOWN');
    return json.data as T;
  }
  return {
    definition: (loc) => call('GET', `/v1/locations/${encodeURIComponent(loc)}/anketa`),
    // Not retried automatically: the client presses "Send" again (the server dedups the CRM card by phone).
    submit: (loc, form) => call('POST', `/v1/locations/${encodeURIComponent(loc)}/anketa`, form),
    recognize: (loc, phone) => call('POST', `/v1/locations/${encodeURIComponent(loc)}/anketa/recognize`, { phone }),
  };
}
